import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // SusTek Custom Variants
        teal: "bg-teal text-teal-foreground hover:bg-teal/90 shadow-sm hover:shadow-md transition-shadow",
        "teal-outline": "border-2 border-teal text-teal bg-transparent hover:bg-teal/10",
        premium: "bg-gradient-primary text-primary-foreground shadow-md hover:shadow-lg transition-shadow",
        hero: "bg-teal/10 text-teal border border-teal/30 hover:bg-teal/20 backdrop-blur-sm",
        "hero-primary": "bg-teal text-teal-foreground hover:bg-teal/90 shadow-lg hover:shadow-xl transition-shadow",
        "nav-cta": "bg-teal text-teal-foreground hover:bg-teal/90 font-semibold shadow-sm",
        sage: "bg-accent text-accent-foreground hover:bg-accent/90",
        navy: "bg-navy text-navy-foreground hover:bg-navy/90",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-lg px-8 text-base",
        xl: "h-12 rounded-lg px-10 text-base font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    (<Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />)
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
